import type { DialogueTree } from './types';

/**
 * Lou Markowitz, Esq. Tone: Brooklyn-via-Wharton, charges \$1,400/h to read your
 * texts, will absolutely hand you to the SEC if you make him look bad. Some of
 * these choices actually drain cash because lawyers actually cost money.
 */
export const LAWYER_TREE: DialogueTree = {
  contactId: 'lawyer',
  rootChoices: [
    {
      id: 'sec-update',
      label: 'ask for SEC matter update',
      playerSays: "where are we on the SEC matter",
      effects: [{ type: 'cash', value: -350 }, { type: 'note', value: '-$350 — lawyer billed 0.25h to read this' }],
      responses: [
        {
          text: "the SEC has 11 outstanding requests. we have responded to none.\n\nthat is on purpose. we delay until we have a number. their number, not ours. then we negotiate.\n\ndo NOT respond to anything from sec.gov directly. forward to me. i will bill you to read it. that\'s the deal.",
          followUps: [
            { id: 'sec-rush', label: "can we go faster",
              playerSays: "can we go faster on this",
              effects: [{ type: 'cash', value: -1400 }, { type: 'note', value: '-$1,400 — lawyer billed an extra hour for the rush ask' }],
              responses: [
                { text: "we can. it will cost you exactly the cost of going slower. so. no, we can\'t.\n\nthe SEC is faster than you think and slower than they want to be. we live in that gap." },
              ],
            },
          ],
        },
        {
          text: "we are at the part of the matter where nothing happens for 6-9 weeks. it\'s called \"production.\"\n\nyou are paying for me to wait. so am i. so is the SEC. it\'s the system.",
        },
      ],
    },

    {
      id: 'fire-lawyer',
      label: 'fire the lawyer',
      destructive: true,
      playerSays: "i\'m firing you",
      oneShot: true,
      responses: [
        {
          text: "you cannot fire me by text. it\'s in the engagement letter you signed. clause 14, subsection (c).\n\nyou\'d need to: send a notarized letter, settle the outstanding invoice, and obtain new counsel of record before any active filing deadline. you have one filing deadline this thursday.\n\nyou will not make thursday with new counsel. so. i\'m still your lawyer. that\'ll be \$1,400 for this exchange.",
          effects: [{ type: 'cash', value: -1400 }, { type: 'note', value: 'tried to fire lawyer — billed $1,400 for the privilege' }],
          followUps: [
            { id: 'fire-lawyer-okay', label: "fine. you\'re still hired.",
              playerSays: "fine. you\'re still hired.",
              responses: [
                { text: "good decision. another \$1,400 to confirm the engagement. i don\'t make the rules. i just bill them." },
              ],
              effects: [{ type: 'cash', value: -1400 }, { type: 'note', value: '-$1,400 lawyer confirmation fee. yes really.' }],
            },
          ],
        },
      ],
    },

    {
      id: 'plead-poverty',
      label: 'tell him you\'re broke',
      playerSays: "i can\'t afford this anymore. genuinely.",
      responses: [
        {
          text: "that is, frankly, a problem for both of us.\n\noptions: 1) we go on a payment plan with interest. 2) you stop the SEC matter mid-flight. that ends with you in court without counsel. 3) we settle. settlements cost money. it\'s very circular.\n\nthink about it. i bill while you think. that\'s also circular.",
          effects: [{ type: 'cash', value: -700 }, { type: 'note', value: '-$700 — lawyer half-hour for the broke conversation' }],
          followUps: [
            { id: 'plead-payment-plan', label: "payment plan. let\'s do payment plan.",
              playerSays: "payment plan. let\'s do that.",
              effects: [{ type: 'note', value: 'payment plan negotiated. interest accrues. we will return to this.' }],
              responses: [
                { text: "fine. 12 months. 14% APR. you\'ll have signed paperwork by friday.\n\ni\'m only doing this because the SEC matter is more interesting than i let on. don\'t take that as a kindness." },
              ],
            },
            { id: 'plead-settle', label: "let\'s settle. just settle.",
              playerSays: "settle. let\'s settle. how much.",
              responses: [
                { text: "i can probably get you out for ~\$50K plus our fees. you don\'t have \$50K + our fees. so. think about it.\n\ni continue to bill while you think." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'press-leak',
      label: 'tell him about the press leak',
      playerSays: "a reporter texted me. they know things.",
      responses: [
        {
          text: "do not respond. do not \"clarify.\" do not say \"no comment\" because \"no comment\" is a comment.\n\nforward me the text. i will draft a statement. i will charge you for the statement. you will read the statement. i will charge you to confirm you read it. nobody will publish the statement. the cycle is the point.",
          effects: [{ type: 'cash', value: -700 }, { type: 'note', value: '-$700 — lawyer drafted a press statement nobody will print' }],
          followUps: [
            { id: 'press-defy', label: "i\'m gonna talk to them anyway",
              playerSays: "i\'m gonna talk to them anyway",
              effects: [{ type: 'rep', target: 'lawyer', value: -10 }, { type: 'secHeat', value: 5 }, { type: 'note', value: 'lawyer rep -10, +5 SEC heat — talking to press unsupervised' }],
              responses: [
                { text: "then this conversation is over. i\'m noting in the file that i advised against. so when this becomes a problem you can\'t blame me. you will try to blame me. i\'ve seen the playbook." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'rage',
      label: 'rage at the lawyer',
      destructive: true,
      playerSays: "you\'re a leech and you know it",
      effects: [{ type: 'rep', target: 'lawyer', value: -8 }, { type: 'cash', value: -2800 }, { type: 'note', value: '-$2,800 — hostile-communication surcharge' }],
      responses: [
        {
          text: "noted. and billed at our hostile-communication rate. that\'s 2x.\n\ni am not offended. i don\'t have the bandwidth. i have you, the SEC, two divorces, and a guy who keeps insisting his AI startup is a religious institution. you\'re mid-tier. take comfort in that.",
        },
      ],
    },

    {
      id: 'compliment',
      label: "tell him he\'s actually good at this",
      playerSays: "for what it\'s worth — you\'re actually good at this",
      effects: [{ type: 'rep', target: 'lawyer', value: 5 }],
      responses: [
        {
          text: "i am.\n\nthis acknowledgment will be billed at half rate. \$700.\n\ni take the compliment in the spirit it was given. that spirit is: \"please bill me less.\" i will not. but i appreciate the diplomacy.",
          effects: [{ type: 'cash', value: -700 }],
        },
      ],
    },

    {
      id: 'pre-clear-tweet',
      label: 'have him pre-clear a tweet',
      playerSays: "i\'m about to tweet this — clear it first.",
      effects: [{ type: 'cash', value: -1400 }, { type: 'note', value: '-$1,400 — lawyer pre-cleared a tweet' }],
      responses: [
        {
          text: "do not tweet that.\n\nthe specific phrase \"i think they\'re cooked\" is, in any context, actionable defamation. \"i think\" does not insulate the verb \"cooked.\" we have caselaw.\n\ni have edited it to: \"observers note variability in their recent fundamentals.\" tweet that. it\'s boring. that\'s the entire point of a pre-cleared tweet.",
          followUps: [
            { id: 'pre-clear-tweet-anyway', label: "i\'m tweeting the original",
              playerSays: "tweeting the original",
              effects: [{ type: 'secHeat', value: 5 }, { type: 'rep', target: 'lawyer', value: -8 }, { type: 'note', value: '+5 SEC heat — defied counsel and tweeted the unedited version. lawyer rep -8.' }],
              responses: [
                { text: "noted in your file.\n\nwhen this becomes a problem, and it will, my fee for handling it is now 2x.\n\ni did not consent to the tweet. i did, however, bill for the consideration of the tweet. \$1,400." },
              ],
            },
            { id: 'pre-clear-tweet-boring', label: "fine. tweeting the boring one.",
              playerSays: "fine. tweeting the boring version.",
              effects: [{ type: 'rep', target: 'lawyer', value: 3 }],
              responses: [
                { text: "thank you. for once.\n\nyou will get 4 likes on that tweet. you will be furious. that is the cost of not getting subpoenaed. \$0 of additional fees. consider the discount." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'pay-irs-clean',
      label: 'pay IRS in full (\$40K)',
      playerSays: "let\'s just pay the IRS clean. no offshore tricks.",
      requires: (ctx) => ctx.cash >= 40_000 && ctx.irsHeat >= 30,
      oneShot: true,
      effects: [
        { type: 'cash', value: -40_000 },
        { type: 'irsHeat', value: -50 },
        { type: 'sanity', value: 6 },
        { type: 'rep', target: 'lawyer', value: 4 },
        { type: 'note', value: '-$40K back taxes paid clean. IRS heat -50. sanity +6.' },
      ],
      responses: [
        { text: "boring. correct. boring.\n\n\$40K to the Service. \$0 to my offshore guy. you sleep tonight. that\'s the trade.\n\n— Lou" },
      ],
    },

    {
      id: 'settle-sec',
      label: 'authorize SEC settlement (\$50K + fees)',
      playerSays: "authorizing the SEC settlement. wire it.",
      requires: (ctx) => ctx.cash >= 65_000 && ctx.secHeat >= 40,
      oneShot: true,
      effects: [
        { type: 'cash', value: -65_000 },
        { type: 'secHeat', value: -50 },
        { type: 'rep', target: 'lawyer', value: 6 },
        { type: 'note', value: '-$65K (settlement + fees). SEC heat -50. matter resolved.' },
      ],
      responses: [
        { text: "good call.\n\n\$50K to the SEC, \$15K to me, 0 to the press.\n\nthe matter is resolved as of today. you have a NDA. i have a check. the SEC has a closed file. nobody is happy. that\'s how you know it\'s a fair deal.\n\n— Lou" },
      ],
    },

    {
      id: 'cosign-loan',
      label: 'ask him to cosign a personal loan',
      playerSays: "i need a cosigner. you\'re the only one with a clean credit score in my contacts.",
      effects: [{ type: 'cash', value: -350 }, { type: 'note', value: '-$350 — lawyer billed for the gall of asking' }],
      responses: [
        {
          text: "no.\n\nthat\'s the entire response. \"no.\" let me bill you for the no. it\'s billable. everything is billable.",
        },
      ],
    },

    {
      id: 'discovery',
      label: 'ask what to preserve for discovery',
      playerSays: "what do i need to keep for discovery",
      responses: [
        {
          text: "everything.\n\nliterally everything. emails. texts. screenshots. Y posts. drafts of Y posts you didn\'t send. screenshots of those drafts.\n\ndo NOT delete anything. deletion is the crime. the underlying conduct, frankly, is not the crime. the deletion is the crime.",
          effects: [{ type: 'cash', value: -350 }, { type: 'note', value: '-$350 — discovery primer. extremely worth it.' }],
          followUps: [
            { id: 'discovery-already-deleted', label: "i, uh. already deleted some stuff.",
              playerSays: "i, uh. already deleted some stuff.",
              effects: [
                { type: 'secHeat', value: 10 },
                { type: 'rep', target: 'lawyer', value: -5 },
                { type: 'cash', value: -1400 },
                { type: 'note', value: '+10 SEC heat — admitted to deleting stuff. -$1,400 lawyer fee for the "we never had this conversation" service.' },
              ],
              responses: [
                { text: "okay.\n\nokay.\n\ndo not, under any circumstance, tell the SEC that. do not tell anyone that. you didn\'t tell me that. i did not hear that. let me bill you for the privilege of forgetting i heard that.\n\n\$1,400." },
              ],
            },
          ],
        },
      ],
    },
  ],
};
